import React from 'react'
import { motion } from 'framer-motion'
import './Projects.css'

const Projects = () => {
  const projects = [
    {
      id: 1,
      title: 'Delben Schools Portal',
      description: 'A comprehensive school management system with student enrollment, grade tracking, and parent communication features. Built with modern web technologies for optimal performance.',
      image: '/projects/delben.png',
      technologies: ['React.js', 'Node.js', 'MongoDB', 'Express.js'],
      liveLink: 'https://delbenschools.com',
      githubLink: 'https://github.com/onyemarvellous',
      featured: true
    },
    {
      id: 2,
      title: 'Mercan School Platform',
      description: 'An interactive educational platform featuring course management, student progress tracking, and real-time collaboration tools for enhanced learning experiences.',
      image: '/projects/mercan.png',
      technologies: ['Next.js', 'TypeScript', 'PostgreSQL', 'Tailwind'],
      liveLink: 'https://mercanschool.com',
      githubLink: 'https://github.com/onyemarvellous',
      featured: true
    },
    {
      id: 3,
      title: 'De Sons Voice',
      description: 'A media and content platform with dynamic content management, user engagement features, and seamless multimedia integration for storytelling.',
      image: '/projects/desonsvoice.png',
      technologies: ['React.js', 'Firebase', 'Node.js', 'AWS'],
      liveLink: 'https://desonsvoice.com',
      githubLink: 'https://github.com/onyemarvellous',
      featured: true
    },
    {
      id: 4,
      title: 'TumiCodes Platform',
      description: 'A developer learning platform with interactive coding challenges, project showcases, and community features to help developers grow their skills.',
      image: '/projects/tumicodes.png',
      technologies: ['Next.js', 'MongoDB', 'Express.js', 'React'],
      liveLink: 'https://tumicodes.com',
      githubLink: 'https://github.com/onyemarvellous',
      featured: false
    },
    {
      id: 5,
      title: 'DCLM OAU Portal',
      description: 'A campus fellowship management system with event scheduling, member registration, and communication tools for the Deeper Life Campus Fellowship.',
      image: '/projects/dclm.png',
      technologies: ['React.js', 'Node.js', 'MongoDB', 'Socket.io'],
      liveLink: 'https://dclmoau.com',
      githubLink: 'https://github.com/onyemarvellous',
      featured: false
    },
    {
      id: 6,
      title: 'E-Commerce Dashboard',
      description: 'A modern admin dashboard for e-commerce platforms with analytics, inventory management, and order processing capabilities.',
      image: '/projects/dashboard.png',
      technologies: ['React.js', 'Chart.js', 'Node.js', 'PostgreSQL'],
      liveLink: '#',
      githubLink: 'https://github.com/onyemarvellous',
      featured: false
    }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.2
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  }

  return (
    <section id="projects" className="projects section">
      <div className="container">
        <motion.div
          className="projects-content"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
        >
          <motion.div className="projects-header" variants={itemVariants}>
            <span className="section-label">Projects</span>
            <h2 className="section-title">
              Featured <span className="text-gradient">Work</span>
            </h2>
            <p className="section-subtitle">
              A showcase of my recent projects, demonstrating my expertise in building
              modern, scalable, and user-friendly web applications.
            </p>
          </motion.div>

          <div className="projects-grid">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                className={`project-card glass-card ${project.featured ? 'featured' : ''}`}
                variants={itemVariants}
                whileHover={{ y: -10 }}
              >
                <div className="project-image">
                  <div className="image-placeholder">
                    <span className="project-number">0{index + 1}</span>
                  </div>
                  <div className="project-overlay">
                    <div className="project-links">
                      <a
                        href={project.liveLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="project-link"
                        title="View Live"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"/>
                        </svg>
                      </a>
                      <a
                        href={project.githubLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="project-link"
                        title="View Code"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/>
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
                <div className="project-content">
                  <h3 className="project-title">{project.title}</h3>
                  <p className="project-description">{project.description}</p>
                  <div className="project-technologies">
                    {project.technologies.map((tech, techIndex) => (
                      <span key={techIndex} className="tech-tag">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div className="projects-cta" variants={itemVariants}>
            <a
              href="https://github.com/onyemarvellous"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/>
              </svg>
              <span>View All Projects on GitHub</span>
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

export default Projects
